import type {
  Activity,
  ActivityLogEntry,
  Announcement,
  Department,
  Manager,
  QuickLink,
  StaffDocument,
} from "@amazing-care/types";
import {
  currentUser,
  activities as seedActivities,
  recentActivityLog as seedActivityLog,
  announcements as seedAnnouncements,
  departments as seedDepartments,
  documents as seedDocuments,
  managers as seedManagers,
  quickLinks as seedQuickLinks,
} from "@/mocks/staff-data";
import type { StaffDataSource } from "./data-source.types";

/**
 * In-memory implementation backing local development. Replaces the four mutable
 * React contexts the prototype used. State lives for the page session only.
 */
function clone<T>(value: T): T {
  return structuredClone(value);
}

function nextId(prefix: string): string {
  return `${prefix}-${Math.random().toString(36).slice(2, 10)}`;
}

function describeFile(file: File): NonNullable<StaffDocument["file"]> {
  return {
    name: file.name,
    mimeType: file.type || "application/octet-stream",
    sizeBytes: file.size,
  };
}

export function createMockDataSource(): StaffDataSource {
  const departments: Department[] = clone(seedDepartments);
  let documents: StaffDocument[] = clone(seedDocuments);
  let announcements: Announcement[] = clone(seedAnnouncements);
  let activities: Activity[] = clone(seedActivities);
  let managers: Manager[] = clone(seedManagers);
  let activityLog: ActivityLogEntry[] = clone(seedActivityLog);
  let quickLinks: QuickLink[] = clone(seedQuickLinks);
  // Uploaded files, kept as Blobs for the session the way SharePoint keeps them.
  const files = new Map<string, Blob>();
  // The prototype seeded everything except the two newest as already read, so
  // the unread badge has something to show on a first visit.
  const UNREAD_SEED_COUNT = 2;
  const readAnnouncementIds = new Set<string>(
    announcements.slice(UNREAD_SEED_COUNT).map((announcement) => announcement.id)
  );

  function departmentLabel(departmentId: string): string {
    return departments.find((department) => department.id === departmentId)?.name ?? departmentId;
  }

  function logDocumentChange(action: ActivityLogEntry["action"], document: StaffDocument): void {
    activityLog = [
      {
        id: nextId("log"),
        actorName: currentUser.name,
        action,
        documentName: document.name,
        departmentLabel: departmentLabel(document.departmentId),
        timestamp: new Date().toISOString(),
      },
      ...activityLog,
    ];
  }

  return {
    async listDepartments() {
      return clone(departments);
    },

    async getDepartment(departmentId) {
      return clone(departments.find((department) => department.id === departmentId) ?? null);
    },

    async listDocuments() {
      return clone(documents);
    },

    async getDocument(documentId) {
      return clone(documents.find((document) => document.id === documentId) ?? null);
    },

    async addDocument(input, file) {
      if (input.content.kind === "file" && !file) throw new Error("Choose a file to upload.");
      const now = new Date().toISOString();
      const created: StaffDocument = {
        ...clone(input),
        id: nextId("doc"),
        lastUpdated: now,
        createdAt: now,
        ...(file ? { content: { kind: "file" }, file: describeFile(file) } : {}),
      };
      if (file) files.set(created.id, file);
      documents = [created, ...documents];
      logDocumentChange("uploaded", created);
      return clone(created);
    },

    async updateDocument(documentId, input, file) {
      const existing = documents.find((document) => document.id === documentId);
      if (!existing) throw new Error("That document no longer exists.");
      const changed: StaffDocument = {
        ...existing,
        ...clone(input),
        id: documentId,
        ...(file ? { content: { kind: "file" }, file: describeFile(file) } : {}),
      };
      // In the database's order: the edit trigger fires before the file
      // function logs the new version. Featuring a document is not an edit,
      // and a document without a visibility is everyone's.
      const edited =
        existing.name !== changed.name ||
        existing.category !== changed.category ||
        existing.subDivisionId !== changed.subDivisionId ||
        (existing.visibility ?? "everyone") !== (changed.visibility ?? "everyone") ||
        JSON.stringify(existing.content) !== JSON.stringify(changed.content);
      const updated: StaffDocument =
        edited || file ? { ...changed, lastUpdated: new Date().toISOString() } : changed;
      if (edited) logDocumentChange("updated", updated);
      if (file) {
        files.set(documentId, file);
        logDocumentChange("replaced", updated);
      }
      documents = documents.map((document) => (document.id === documentId ? updated : document));
      return clone(updated);
    },

    async deleteDocument(documentId) {
      const removed = documents.find((document) => document.id === documentId);
      documents = documents.filter((document) => document.id !== documentId);
      files.delete(documentId);
      if (removed) logDocumentChange("deleted", removed);
    },

    async getDocumentFile(documentId) {
      const stored = files.get(documentId);
      if (stored) return stored;
      const document = documents.find((item) => item.id === documentId);
      if (document?.content.kind === "file" && document.content.url) {
        return (await fetch(document.content.url)).blob();
      }
      throw new Error("This document has no file.");
    },

    async getDocumentPreview() {
      // Converting Office files is SharePoint's job; the fixture has none.
      throw new Error("Previews of Word, Excel and PowerPoint files need SharePoint.");
    },

    async getDocumentLink(documentId) {
      const stored = files.get(documentId);
      if (stored) return URL.createObjectURL(stored);
      const document = documents.find((item) => item.id === documentId);
      if (document?.content.kind === "file" && document.content.url) return document.content.url;
      throw new Error("This document has no file.");
    },

    async listAnnouncements() {
      return clone(announcements);
    },

    async addAnnouncement(input) {
      const created: Announcement = {
        ...clone(input),
        id: nextId("ann"),
        timestamp: new Date().toISOString(),
      };
      announcements = [created, ...announcements];
      return clone(created);
    },

    async updateAnnouncement(id, input) {
      const updated: Announcement = { ...clone(input), id, timestamp: new Date().toISOString() };
      announcements = announcements.map((item) => (item.id === id ? updated : item));
      return clone(updated);
    },

    async deleteAnnouncement(id) {
      announcements = announcements.filter((item) => item.id !== id);
    },

    async listReadAnnouncementIds() {
      return [...readAnnouncementIds];
    },

    async markAnnouncementRead(id) {
      readAnnouncementIds.add(id);
    },

    async markAllAnnouncementsRead() {
      for (const announcement of announcements) readAnnouncementIds.add(announcement.id);
    },

    async listActivities() {
      return clone(activities);
    },

    async getActivity(activityId) {
      return clone(activities.find((activity) => activity.id === activityId) ?? null);
    },

    async addActivity(input) {
      const created: Activity = { ...clone(input), id: nextId("act") };
      activities = [created, ...activities];
      return clone(created);
    },

    async updateActivity(id, input) {
      const updated: Activity = { ...clone(input), id };
      activities = activities.map((item) => (item.id === id ? updated : item));
      return clone(updated);
    },

    async deleteActivity(id) {
      activities = activities.filter((item) => item.id !== id);
    },

    async listManagers() {
      return clone(managers);
    },

    async addManager(input) {
      const created: Manager = { ...clone(input), id: nextId("mgr") };
      managers = [...managers, created];
      return clone(created);
    },

    async updateManager(id, input) {
      const updated: Manager = { ...clone(input), id };
      managers = managers.map((item) => (item.id === id ? updated : item));
      return clone(updated);
    },

    async deleteManager(id) {
      managers = managers.filter((item) => item.id !== id);
    },

    async listActivityLog() {
      return clone(activityLog);
    },

    async listQuickLinks() {
      return clone(quickLinks);
    },

    async addQuickLink(input) {
      const created: QuickLink = { ...clone(input), id: nextId("link") };
      quickLinks = [created, ...quickLinks];
      return clone(created);
    },

    async updateQuickLink(id, input) {
      if (!quickLinks.some((link) => link.id === id))
        throw new Error("That link no longer exists.");
      const updated: QuickLink = { ...clone(input), id };
      quickLinks = quickLinks.map((link) => (link.id === id ? updated : link));
      return clone(updated);
    },

    async deleteQuickLink(id) {
      quickLinks = quickLinks.filter((link) => link.id !== id);
    },
  };
}
